import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateRelay, useDrives, useRelays } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { RelayCreateResult, RelayDownload } from '@vivport/backend/types';
import { CheckCircle2, Copy, Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type AddRelayDialogProps = {
  memberId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClosed?: (created: RelayCreateResult | null) => void;
};

function CommandRow({ command }: { command: string }) {
  const copy = async () => {
    await navigator.clipboard.writeText(command);
    toast.success('Command copied.');
  };
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border bg-muted/30 px-3 py-2 text-sm">
        {command}
      </code>
      <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
        <Copy className="mr-1 h-3.5 w-3.5" />
        Copy
      </Button>
    </div>
  );
}

function DownloadButton({ download }: { download: RelayDownload }) {
  if (!download.available) {
    return (
      <Button type="button" variant="outline" disabled title="Not published on this server">
        <Download className="mr-1 h-4 w-4" />
        {download.label}
      </Button>
    );
  }
  return (
    <Button asChild variant={download.kind === 'windows' ? 'default' : 'outline'}>
      <a href={download.url} download={download.filename}>
        <Download className="mr-1 h-4 w-4" />
        {download.label}
      </a>
    </Button>
  );
}

export function AddRelayDialog({ memberId, open, onOpenChange, onClosed }: AddRelayDialogProps) {
  const create = useCreateRelay();
  const [name, setName] = useState('');
  const [created, setCreated] = useState<RelayCreateResult | null>(null);
  const relays = useRelays(memberId, { refetchInterval: created ? 3_000 : 30_000 });
  const drives = useDrives(memberId, { refetchInterval: created ? 3_000 : 30_000 });
  const [showAdvanced, setShowAdvanced] = useState(false);

  const live = created ? (relays.data?.relays ?? []).find((relay) => relay.id === created.relay.id) : undefined;
  const paired = live?.status === 'ACTIVE';
  const folders = created ? (drives.data?.drives ?? []).filter((drive) => drive.sourceId === created.relay.id) : [];

  const close = () => {
    const result = created;
    setName('');
    setCreated(null);
    setShowAdvanced(false);
    onOpenChange(false);
    onClosed?.(result);
  };

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    create.mutate(
      { name: trimmed },
      {
        onSuccess: (result) => setCreated(result),
        onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to create the relay.')),
      }
    );
  };

  const nonePublished = created !== null && created.downloads.every((download) => !download.available);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        {created === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Add folder</DialogTitle>
              <DialogDescription>
                Name the computer or server that holds the folder. You will then download a small program to run on
                it.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className="space-y-2">
                <Label htmlFor="relay-name">Computer name</Label>
                <Input
                  id="relay-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="OFFICE-SERVER"
                  maxLength={64}
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="button" onClick={handleCreate} disabled={!name.trim() || create.isPending}>
                {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Continue
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Set up '{created.relay.name}'</DialogTitle>
              <DialogDescription>
                Download the relay program onto that computer and run it. It pairs itself with this hub, opens a page
                where you pick the folders to share, and installs itself to start at logon. Shared folders appear
                here by themselves.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {created.downloads.map((download) => (
                    <DownloadButton key={download.kind} download={download} />
                  ))}
                </div>
                {nonePublished ? (
                  <p className="text-xs text-destructive">
                    This server does not publish relay downloads yet. Ask your Vivport administrator for the relay
                    program.
                  </p>
                ) : null}
                <p className="text-xs text-muted-foreground">
                  The download is personalised for this relay and valid for 15 minutes. Run it as a user who can read
                  the folders. The Node.js script runs with <code>node</code> on a machine that has Node.js 20 or newer.
                </p>

                <div className="rounded-md border p-3">
                  {folders.length > 0 ? (
                    <p className="flex items-center gap-2 text-sm text-success">
                      <CheckCircle2 className="h-4 w-4" />
                      {folders.length === 1
                        ? `Folder '${folders[0].name}' connected. Press Done.`
                        : `${folders.length} folders connected: ${folders.map((drive) => drive.name).join(', ')}. Press Done.`}
                    </p>
                  ) : paired ? (
                    <p className="flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Paired with {live?.hostname ?? 'the computer'}. Waiting for a shared folder...
                    </p>
                  ) : (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Waiting for the relay to pair... this updates by itself.
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  className="text-xs text-muted-foreground underline"
                  onClick={() => setShowAdvanced((value) => !value)}
                >
                  {showAdvanced ? 'Hide command-line option' : 'Prefer the command line, or renamed the file?'}
                </button>
                {showAdvanced ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Pairing code <span className="font-mono text-sm tracking-widest">{created.pairingCode}</span>.
                      Enter it on the setup page, or pair from a terminal:
                    </p>
                    <CommandRow command={created.commands.pair} />
                    <CommandRow command={created.commands.share} />
                    <CommandRow command={created.commands.start} />
                  </div>
                ) : null}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" onClick={close}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
